import React, { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import type {
  RegistrationData,
  UserData,
  EligibilityResult,
  EligibilityTrackResult,
} from '@/types/registration';
import { encryptData, decryptData } from '@/utils/crypto';
import { generateAccessWebregUserId, WEBREG_USER_ID_STORAGE_KEY } from '@/utils/webregUserIdGenerator';
import { saveUTMParams, UTMParams } from '@/utils/utm';
import { generateSampleGroup } from '@/utils/abTest';
import analytics from '@/services/Analytics';
import {
  ERROR_DECRYPTION_FAILED,
  ERROR_STORAGE_LOAD_FAILED,
  ERROR_STORAGE_SAVE_FAILED,
} from '@/consts/events';
import { isPilotMock, isPilotE2eRuntime } from '@/services/pilotMock';
import { INITIAL_WREG, type WregFlowSlice } from '@/components/webreg/wregFlowState';

type RegistrationContextValue = {
  registrationData: RegistrationData;
  isLoaded: boolean;
  updateMedicareCoverage: (data: Partial<RegistrationData['medicareCoverage']>) => void;
  updateEligibility: (eligibility: EligibilityResult) => void;
  upsertEligibilityResult: (trackResult: EligibilityTrackResult) => void;
  updateSelectedTracks: (tracks: RegistrationData['selectedTracks']) => void;
  updateConsent: (agreed: boolean) => void;
  updateWreg: (data: Partial<WregFlowSlice>) => void;
  updateLabs: (hasLabs: boolean | null) => void;
  setPromoCodeData: (promoCodeData: RegistrationData['promoCodeData']) => void;
  updateAccount: (data: Partial<RegistrationData['account']>) => void;
  updateShipping: (data: Partial<RegistrationData['shipping']>) => void;
  setUserData: (userData: UserData) => void;
  hasFinishedShipping: boolean;
  setHasFinishedShipping: (finished: boolean) => void;
  clearRegistrationData: () => void;
  webregUserId: string;
  sampleGroup: string;
  utmParams: UTMParams | null;
};

const RegistrationContext = createContext<RegistrationContextValue | undefined>(undefined);

// Storage key for encrypted data
const STORAGE_KEY = 'hh_access_reg_data';

const initialData: RegistrationData = {
  medicareCoverage: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    zipCode: '',
    mbi: '',
    gender: null,
  },
  eligibility: null,
  eligibilityResults: [],
  selectedTracks: [],
  consentAgreed: false,
  wreg: INITIAL_WREG,
  labsCompleted: null,
  account: {
    email: '',
    password: '',
    retypePassword: '',
    phoneNumber: '',
  },
  shipping: {
    firstName: '',
    lastName: '',
    streetAddress: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
  },
  userData: null,
  promoCodeData: null,
};

/**
 * Helper to pick specific fields from an object (whitelist approach)
 */
function pickFields<T, K extends keyof T>(obj: T, keys: readonly K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    result[key] = obj[key];
  });
  return result;
}

/**
 * Type for encrypted storage
 * Excludes: password, retypePassword, healthConditions (deprecated field - never stored)
 */
type StoredData = {
  medicareCoverage: Pick<RegistrationData['medicareCoverage'], 'firstName' | 'lastName' | 'dateOfBirth' | 'zipCode' | 'mbi' | 'gender' | 'promoCode'>;
  // crisisGateShown excluded — it's a near-direct proxy for a Q9 suicidality
  eligibility: RegistrationData['eligibility'];
  eligibilityResults: RegistrationData['eligibilityResults'];
  selectedTracks: RegistrationData['selectedTracks'];
  consentAgreed: RegistrationData['consentAgreed'];
  wreg: RegistrationData['wreg'];
  labsCompleted: RegistrationData['labsCompleted'];
  account: Pick<RegistrationData['account'], 'email' | 'phoneNumber'>;
  shipping: RegistrationData['shipping'];
  // RND-36530: promoCodeData was memory-only (not in this whitelist), so any
  // mid-flow reload/remount nulled it and a later `promoCodeData!.code`
  // non-null assertion threw. Promo/program config only — not patient PHI.
  promoCodeData: RegistrationData['promoCodeData'];
  // Pilot-mock / E2E only (read + write gated by isPilotE2eRuntime()): authToken +
  // solutions so Playwright can open /shipping without re-walking OTP. Never on
  // deployable staging. AES-GCM here is at-rest obfuscation (key is in the
  // bundle), not protection from same-origin script.
  // Do not store email/phone/name here — only what remount seeding needs.
  userData?: Pick<NonNullable<RegistrationData['userData']>, 'authToken' | 'solutions'>;
};

/**
 * Load persisted data from sessionStorage
 * - All data encrypted with AES-GCM
 * - Never stored: Passwords
 * - Stored: MBI (medicareCoverage.mbi) — needed to resume mid-flow after remount
 *   without re-collecting; at-rest AES-GCM only (bundle key). Same retention
 *   class as raw PHQ-9/GAD-7 answers pending PRD 11 §7 risk-acceptance.
 *   pre-launch risk-acceptance on whether raw screener answers should persist
 *   client-side at all (Daniel/Edo). `crisisGateShown` itself is NOT persisted
 *   Q9-suicidality-proxy flag.
 */
const loadPersistedData = async (): Promise<RegistrationData> => {
  // error_class uses `error.name`, never `error.constructor.name` — no
  // esbuild.keepNames in vite.config.ts, so class identifiers are mangled in
  // production builds while string `name` assignments survive (PR #101 review).
  //
  // Three stages, each with its own catch: a storage read failure, a decrypt/
  // parse failure and a rehydration shape error are distinct signals, and a
  // single try around all three reported every one of them as
  // `decryption_failed` while leaving `storage_load_failed` unreachable
  // (PR #101 review). Every stage still fails soft to initialData.
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read persisted registration data:', error);
    analytics.trackError(ERROR_STORAGE_LOAD_FAILED, {
      stage: 'read',
      error_class: error instanceof Error ? error.name : 'UnknownError',
    });
    return initialData;
  }

  if (!stored) {
    return initialData;
  }

  // Persisted payload is untrusted at this point — shape errors are handled by
  // the rehydrate stage below, so the loose type stays deliberate.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let decryptedData: any;
  try {
    decryptedData = await decryptData(stored);
  } catch (error) {
    console.warn('Failed to decrypt persisted registration data:', error);
    analytics.trackError(ERROR_DECRYPTION_FAILED, {
      stage: 'decrypt',
      error_class: error instanceof Error ? error.name : 'UnknownError',
    });
    return initialData;
  }

  try {
    // Merge with initialData, excluding passwords only
    return {
      ...initialData,
      medicareCoverage: {
        ...initialData.medicareCoverage,
        ...decryptedData.medicareCoverage,
      },
      eligibility: decryptedData.eligibility ?? initialData.eligibility,
      eligibilityResults: decryptedData.eligibilityResults ?? initialData.eligibilityResults,
      selectedTracks: decryptedData.selectedTracks ?? initialData.selectedTracks,
      consentAgreed: decryptedData.consentAgreed ?? initialData.consentAgreed,
      wreg: { ...initialData.wreg, ...decryptedData.wreg },
      labsCompleted: decryptedData.labsCompleted ?? initialData.labsCompleted,
      account: {
        ...initialData.account,
        ...decryptedData.account,
        // Force clear password fields (never persisted)
        password: '',
        retypePassword: '',
      },
      shipping: {
        ...initialData.shipping,
        ...decryptedData.shipping,
      },
      promoCodeData: decryptedData.promoCodeData ?? initialData.promoCodeData,
      // Symmetric with the writer: never rehydrate pilot credentials on
      // deployable staging/production — DEV / e2e only.
      userData:
        isPilotE2eRuntime() && decryptedData.userData?.authToken
          ? {
              authToken: decryptedData.userData.authToken,
              solutions: decryptedData.userData.solutions ?? [],
              email: '',
              phonenumber: '',
            }
          : initialData.userData,
    };
  } catch (error) {
    console.warn('Failed to rehydrate persisted registration data:', error);
    analytics.trackError(ERROR_STORAGE_LOAD_FAILED, {
      stage: 'rehydrate',
      error_class: error instanceof Error ? error.name : 'UnknownError',
    });
    return initialData;
  }
};

const RegistrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [registrationData, setRegistrationData] = useState<RegistrationData>(initialData);
  const [hasFinishedShipping, setHasFinishedShipping] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const saveRequestIdRef = useRef(0);

  // Initialize webregUserId - generate or retrieve from localStorage
  const [webregUserId] = useState<string>(() => {
    return localStorage?.getItem?.(WEBREG_USER_ID_STORAGE_KEY) || generateAccessWebregUserId();
  });

  // Initialize sampleGroup - generate or retrieve from localStorage
  const [sampleGroup] = useState<string>(() => {
    return generateSampleGroup();
  });

  // Initialize UTM params - save from URL or retrieve from localStorage.
  // The parse must not throw: this runs during render of a provider that sits
  // above every route, so an exception here white-screens the app with no error
  // boundary below it — and because the value lives in localStorage rather than
  // sessionStorage, a truncated write (tab killed mid-write, quota eviction)
  // survives reloads and the user cannot recover by refreshing. Drop the poison
  // instead (PR #101 review).
  const [utmParams] = useState<UTMParams | null>(() => {
    const fromUrl = saveUTMParams();
    if (fromUrl) return fromUrl;

    const previousUTMParams = localStorage.getItem('utmParams');
    if (!previousUTMParams) return null;
    try {
      return JSON.parse(previousUTMParams);
    } catch (error) {
      console.warn('Discarding unparseable persisted utmParams:', error);
      localStorage.removeItem('utmParams');
      return null;
    }
  });

  // Deliberately no analytics.registerUser(webregUserId): it registered a
  // `user_id` super property holding the *anonymous* webreg id, which analysts
  // read as the account id. It was never re-registered with the real
  // analyticsID after signup, so `user_id` and `distinct_id` disagreed for every
  // registered user and any board joining on it joined on the wrong key.
  // `useAnalytics` already attaches `webreg_user_id` to every event, and
  // `analytics.identifyUser(analyticsID)` sets distinct_id at signup, so nothing
  // is lost (PR #101 review).

  // Load persisted data on mount
  useEffect(() => {
    loadPersistedData()
      .then(data => {
        setRegistrationData(data);
        setIsLoaded(true);
      })
      .catch(error => {
        console.warn('Failed to load persisted data:', error);
        analytics.trackError(ERROR_STORAGE_LOAD_FAILED, {
          stage: 'apply',
          error_class: error instanceof Error ? error.name : 'UnknownError',
        });
        setIsLoaded(true);
      });
  }, []);

  // Persist data to sessionStorage whenever it changes (after initial load)
  useEffect(() => {
    if (!isLoaded) return; // Skip saving until initial load is complete

    const requestId = ++saveRequestIdRef.current;
    const saveData = async () => {
      try {
        // Whitelist: Only store fields we explicitly allow (excluding passwords and deprecated healthConditions)
        const dataToStore: StoredData = {
          medicareCoverage: pickFields(registrationData.medicareCoverage, ['firstName', 'lastName', 'dateOfBirth', 'zipCode', 'mbi', 'gender', 'promoCode'] as const),
          eligibility: registrationData.eligibility,
          eligibilityResults: registrationData.eligibilityResults,
          selectedTracks: registrationData.selectedTracks,
          consentAgreed: registrationData.consentAgreed,
          wreg: registrationData.wreg,
          labsCompleted: registrationData.labsCompleted,
          account: pickFields(registrationData.account, ['email', 'phoneNumber'] as const),
          shipping: registrationData.shipping,
          promoCodeData: registrationData.promoCodeData,
          ...(isPilotE2eRuntime() && registrationData.userData
            ? {
                userData: {
                  authToken: registrationData.userData.authToken,
                  solutions: registrationData.userData.solutions,
                },
              }
            : {}),
        };

        // Encrypt and store whitelisted data
        const encrypted = await encryptData(dataToStore);
        // Discard this write if a newer save has since been dispatched, so an
        // out-of-order-completing encrypt call can't clobber fresher data.
        if (requestId === saveRequestIdRef.current) {
          sessionStorage.setItem(STORAGE_KEY, encrypted);
        }
      } catch (error) {
        console.warn('Failed to persist registration data:', error);
        analytics.trackError(ERROR_STORAGE_SAVE_FAILED, {
          stage: 'persist',
          error_class: error instanceof Error ? error.name : 'UnknownError',
        });
      }
    };

    saveData();
  }, [registrationData, isLoaded]);

  const updateMedicareCoverage = useCallback((data: Partial<RegistrationData['medicareCoverage']>) => {
    setRegistrationData(prev => ({
      ...prev,
      medicareCoverage: { ...prev.medicareCoverage, ...data },
    }));
  }, []);




  // RND-35692: merge partial screener updates (answers/scores/flags accumulate
  // across the PHQ-9 then GAD-7 steps). Client-side only — never submitted.

  const updateEligibility = useCallback((eligibility: EligibilityResult) => {
    setRegistrationData(prev => ({
      ...prev,
      eligibility,
    }));
  }, []);

  const upsertEligibilityResult = useCallback((trackResult: EligibilityTrackResult) => {
    setRegistrationData(prev => ({
      ...prev,
      eligibilityResults: [
        ...prev.eligibilityResults.filter(r => r.track !== trackResult.track),
        trackResult,
      ],
    }));
  }, []);

  const updateSelectedTracks = useCallback((tracks: RegistrationData['selectedTracks']) => {
    setRegistrationData(prev => ({
      ...prev,
      selectedTracks: tracks,
    }));
  }, []);

  const updateConsent = useCallback((agreed: boolean) => {
    setRegistrationData(prev => ({
      ...prev,
      consentAgreed: agreed,
    }));
  }, []);

  const updateWreg = useCallback((data: Partial<WregFlowSlice>) => {
    setRegistrationData(prev => ({
      ...prev,
      wreg: { ...prev.wreg, ...data },
    }));
  }, []);

  const updateLabs = useCallback((hasLabs: boolean | null) => {
    setRegistrationData(prev => ({
      ...prev,
      labsCompleted: hasLabs,
    }));
  }, []);

  const setPromoCodeData = useCallback((promoCodeData: RegistrationData['promoCodeData']) => {
    setRegistrationData(prev => ({
      ...prev,
      promoCodeData,
    }));
  }, []);

  const updateAccount = useCallback((data: Partial<RegistrationData['account']>) => {
    setRegistrationData(prev => ({
      ...prev,
      account: { ...prev.account, ...data },
    }));
  }, []);

  const clearRegistrationData = useCallback(() => {
    setRegistrationData(initialData);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear persisted registration data:', error);
    }
  }, []);

  const updateShipping = useCallback((data: Partial<RegistrationData['shipping']>) => {
    setRegistrationData(prev => ({
      ...prev,
      shipping: { ...prev.shipping, ...data },
    }));
  }, []);

  const setUserData = useCallback((userData: UserData) => {
    setRegistrationData(prev => ({
      ...prev,
      userData,
    }));
  }, []);

  // Pilot-mock-only E2E hook: Playwright seeds auth + shipping access without
  // walking OTP/signup. Static DEV/e2e guard tree-shakes this out of deployable
  // staging/production builds; isPilotMock() then enforces the mode allowlist
  // (so e.g. `vite --mode preview` with the flag set still stays inert).
  useEffect(() => {
    if (!(import.meta.env.DEV || import.meta.env.MODE === 'e2e')) return undefined;
    if (!isPilotMock() || !isLoaded) return undefined;
    const api = {
      setUserData,
      updateMedicareCoverage,
      updateShipping,
      updateLabs,
      updateAccount,
      updateSelectedTracks,
    };
    (window as Window & { __pilotE2e?: typeof api }).__pilotE2e = api;
    return () => {
      delete (window as Window & { __pilotE2e?: typeof api }).__pilotE2e;
    };
  }, [
    isLoaded,
    setUserData,
    updateMedicareCoverage,
    updateShipping,
    updateLabs,
    updateAccount,
    updateSelectedTracks,
  ]);

  // Navigation helpers



  const value = useMemo(
    () => ({
      registrationData,
      isLoaded,
      updateMedicareCoverage,
      updateEligibility,
      upsertEligibilityResult,
      updateSelectedTracks,
      updateConsent,
      updateWreg,
      updateLabs,
      setPromoCodeData,
      updateAccount,
      updateShipping,
      setUserData,
      hasFinishedShipping,
      setHasFinishedShipping,
      clearRegistrationData,
      webregUserId,
      sampleGroup,
      utmParams,
    }),
    [
      registrationData,
      isLoaded,
      updateMedicareCoverage,
      setPromoCodeData,
      updateAccount,
      updateShipping,
      updateEligibility,
      upsertEligibilityResult,
      updateSelectedTracks,
      updateConsent,
      updateWreg,
      updateLabs,
      setUserData,
      hasFinishedShipping,
      clearRegistrationData,
      webregUserId,
      sampleGroup,
      utmParams,
    ],
  );

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
};

const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

export { RegistrationProvider, useRegistration };
